import { IntegrationType } from '@principle-theorem/integrations';
import {
  isDocRef,
  isEnumValue,
  isObject,
  type DocumentReference,
  type ISoftDelete,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import { IAppointment } from '../appointment/appointment';
import {
  IAutomation,
  type IAutomationConfiguration,
} from '../automation/automation';
import { IContact, IContactEmailContactDetails } from '../contact/contact';
import { IMarketingCampaignEntry } from '../marketing/campaign-entry';
import { IMedia } from '../media/media';
import { IAutomatedNotification } from '../notification/automated-notification';
import { type IPatient } from '../patient/patient';
import { type IPractice } from '../practice/practice';
import { type IStaffer } from '../staffer/staffer';
import { EmailIntegrationOption } from './email-integration';
import { CommunicationDirection } from './sms-message';

export interface ISendgridEmailMetadata {
  provider: IntegrationType.Sendgrid;
}

export type EmailProviderMetadata = ISendgridEmailMetadata;

export enum EmailDeliveryStatus {
  Pending = 'pending',
  Sent = 'sent',
  Delivered = 'delivered',
  Bounced = 'bounced',
  Failed = 'failed',
}

export interface IBaseEmailMessage<T extends IContactEmailContactDetails>
  extends ISoftDelete {
  readBy: DocumentReference<IStaffer>[];
  subject: string;
  text: string;
  html: string;
  recipientEmail: string;
  recipientRef: DocumentReference<T>;
  practiceEmail: string;
  practiceRef: DocumentReference<IPractice>;
  authorRef:
    | DocumentReference<
        IPatient | IStaffer | IPractice | IAutomationConfiguration | IContact
      >
    | EmailIntegrationOption;
  direction: CommunicationDirection;
  attachments?: DocumentReference<IMedia>[];
  automationRef?: DocumentReference<IAutomation<IAutomatedNotification>>;
  replyData?: IEmailReplyTokenData;
  errorMessage?: string;
}

export type EmailMessage<
  T extends IContactEmailContactDetails = IContactEmailContactDetails,
> = IBaseEmailMessage<T> &
  (
    | { status: EmailDeliveryStatus.Pending }
    | {
        status: EmailDeliveryStatus;
        metadata: EmailProviderMetadata;
      }
  );

export enum EmailActionType {
  Reply = 'reply',
  Unsubscribe = 'unsubscribe',
}

export enum EmailResourceType {
  Patient = 'patient',
  Contact = 'contact',
  Appointment = 'appointment',
}

export type IEmailReplyTokenData = {
  uid: string;
  practiceRef: DocumentReference<IPractice>;
  actionType: EmailActionType;
  resourceType: EmailResourceType;
} & (
  | {
      actionType: EmailActionType;
      resourceType: EmailResourceType.Patient;
      resourceRef: DocumentReference<IPatient>;
      campaignEntry?: DocumentReference<IMarketingCampaignEntry>;
    }
  | {
      actionType: EmailActionType.Reply;
      resourceType: EmailResourceType.Appointment;
      resourceRef: DocumentReference<IAppointment>;
    }
  | {
      actionType: EmailActionType.Reply;
      resourceType: EmailResourceType.Contact;
      resourceRef: DocumentReference<IContact>;
    }
);

export function isEmailReplyTokenData(
  data: unknown
): data is IEmailReplyTokenData {
  return (
    isObject(data) &&
    isDocRef(data.practiceRef) &&
    isEnumValue(EmailActionType, data.actionType) &&
    isEnumValue(EmailResourceType, data.resourceType)
  );
}

export interface IUnsubscribeFormRequest {
  tokenUid: string;
}

export function isUnsubscribeFormRequest(
  data: unknown
): data is IUnsubscribeFormRequest {
  return isObject(data) && isString(data.tokenUid);
}
